import { Slot } from '@radix-ui/react-slot';
import classNames from 'classnames';
import React from 'react';

import styles from './button.module.scss';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { CircleNotchIcon } from '@phosphor-icons/react';

export interface ButtonProps extends React.ComponentPropsWithoutRef<'button'> {
    asChild?: boolean;
    children?: React.ReactNode;
    variant?: 'contained' | 'outlined' | 'text';
    size?: 'small' | 'medium' | 'large';
    color?: 'primary' | 'white';
    loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            asChild,
            children,
            variant = 'contained',
            color = 'primary',
            size = 'medium',
            loading,
            ...props
        },
        forwardedRef
    ) => {
        const classes = classNames([
            {
                [styles.primaryContained]: color === 'primary' && variant === 'contained',
                [styles.whiteContained]: color === 'white' && variant === 'contained',
                [styles.primaryOutlined]: color === 'primary' && variant === 'outlined',
                [styles.whiteOutlined]: color === 'white' && variant === 'outlined',
                [styles.primaryText]: color === 'primary' && variant === 'text',
                [styles.primaryTextWhite]: color === 'white' && variant === 'text',
            },
            variant !== 'text' ? styles[size] : '',
            props.className,
        ]);

        const Comp = asChild ? Slot : 'button';

        return (
            <Comp {...props} ref={forwardedRef} className={classes}>
                {loading ? (
                    <>
                        <span style={{ display: 'contents', visibility: 'hidden' }} aria-hidden>
                            {children}
                        </span>
                        <VisuallyHidden>{children}</VisuallyHidden>
                        <div className={styles.loadingContainer}>
                            <CircleNotchIcon size={24} weight="bold" />
                        </div>
                    </>
                ) : (
                    children
                )}
            </Comp>
        );
    }
);

Button.displayName = 'Button';

export default Button;
